import { useEffect, useRef } from 'react';
import { animate, useReducedMotion, type AnimationPlaybackControls } from 'motion/react';

export const focusUnitAnchorId = (unitId: string) => `focus-unit-card-${unitId}`;

// Gap left above the focus card once it lands, clearing the fixed header with breathing room.
const SCROLL_TOP_MARGIN = 120;
// Apple-like deceleration: fast start, long eased tail (ease-out-expo).
const EASE_OUT_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1];
const DURATION = 0.9;
// Beat before the scroll begins, so the card list settles and the student registers the page
// before it glides — scrolling the instant the page renders feels abrupt.
const SETTLE_DELAY_MS = 350;

export const useScrollFocusUnitIntoView = (focusUnitId: string | null): void => {
  const hasScrolledRef = useRef(false);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    if (!focusUnitId || hasScrolledRef.current) return;

    let controls: AnimationPlaybackControls | null = null;

    const timeoutId = window.setTimeout(() => {
      const el = document.getElementById(focusUnitAnchorId(focusUnitId));
      if (!el) return;

      hasScrolledRef.current = true;
      const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - SCROLL_TOP_MARGIN);

      if (shouldReduceMotion) {
        window.scrollTo({ top, behavior: 'auto' });
        return;
      }

      controls = animate(window.scrollY, top, {
        duration: DURATION,
        ease: EASE_OUT_EXPO,
        onUpdate: v => window.scrollTo(0, v),
      });
    }, SETTLE_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
      controls?.stop();
    };
  }, [focusUnitId, shouldReduceMotion]);
};
